import {
  ContainerClusterEntity,
  containersSelector,
  useGetContainersByContainerClusterQuery,
  useGetWellsByContainerClusterQuery,
  wellsSelector
} from "@ots-frontend/ots-data";
import {ActionIcon, Card, Container, Divider, Grid, Group, Modal, Text, Title} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {ObjectMappingDataTableCard} from "../../card/ObjectMappingDataTableCard/ObjectMappingDataTableCard";
import {HasAccess} from "../../auth/HasAccess/HasAccess";
import {EntityAnchor} from "../../anchor/EntityAnchor/EntityAnchor";
import {RestrictionDataTableCard} from "../../card/RestrictionDataTableCard/RestrictionDataTableCard";
import {useMemo} from "react";
import {skipToken} from "@reduxjs/toolkit/query";
import {ContainerDataTable} from "../../data-table/ContainerDataTable/ContainerDataTable";
import {useApp} from "../../../providers/RegionProvider/RegionProvider";
import { WellDataTable } from "../../data-table/WellDataTable/WellDataTable";
import {useDisclosure} from "@mantine/hooks";
import {IconHierarchy} from "@tabler/icons-react";
import {EntityGraphView} from "../../graph/EntityGraphView/EntityGraphView";

export type ContainerClusterDetailsProps = {
  containerCluster: ContainerClusterEntity
}

export const ContainerClusterDetails = (props: ContainerClusterDetailsProps) => {
  const {containerCluster} = props;

  const {t} = useTranslation(["common"]);
  const {regionId} = useApp();

  const [opened, { close, open }] = useDisclosure(false);

  const selectContainers = useMemo(() => {
    return containersSelector();
  }, []);

  const {containers, isContainersLoading} = useGetContainersByContainerClusterQuery(regionId ? {
    regionId: regionId,
    containerClusterId: containerCluster.id,
  } : skipToken, {
    selectFromResult: (result) => ({
      containers: selectContainers(result, {showDoNotUse: true}),
      isContainersLoading: result.isLoading
    })
  });

  const selectWells = useMemo(() => {
    return wellsSelector();
  }, []);

  const {wells, isWellsLoading} = useGetWellsByContainerClusterQuery(regionId ? {
    regionId: regionId,
    containerClusterId: containerCluster.id,
  } : skipToken, {
    selectFromResult: (result) => ({
      wells: selectWells(result, {showDoNotUse: true}),
      isWellsLoading: result.isLoading
    })
  });

  return (
    <Grid>
      <Grid.Col span={6}>
        <Card shadow="sm" padding="lg" radius="md" withBorder>

          <Group position="apart" pb="sm">
            <Title order={4}>{t("profile.containercluster.info.section.details.label", "Details")}</Title>
            <ActionIcon onClick={() => open()}>
              <IconHierarchy size="1.125rem"/>
            </ActionIcon>
          </Group>

          <Divider/>

          <Grid>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.containercluster.info.field.name.label", "Name")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {containerCluster.name}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.containercluster.info.field.equalized.label", "Equalized")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {containerCluster.equalized ? "Yes" : "No"}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.containercluster.info.field.site.label", "Site")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {containerCluster.siteId ? (
                  <EntityAnchor
                    value={{
                      objectId: containerCluster.siteId,
                      objectName: containerCluster.siteName,
                      objectType: 'SITE'
                    }}
                    icon={{
                      size: 14
                    }}/>
                ) : undefined}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.containercluster.info.field.afe.label", "AFE")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {containerCluster.afe &&
                  <>
                    {containerCluster.afePrefix && containerCluster.afePrefix}
                    <Text fw={700} inherit={true} span={true}>
                      {` ${containerCluster.afe} `}
                    </Text>
                    {containerCluster.afeSuffix && containerCluster.afeSuffix}
                  </>
                }
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.containercluster.info.field.leaseid.label", "Lease ID")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {containerCluster.leaseId}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.containercluster.info.field.leasename.label", "Lease Name")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {containerCluster.leaseName}
              </Text>
            </Grid.Col>
          </Grid>
        </Card>
      </Grid.Col>

      <Grid.Col span={12}>
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group position="apart" pb="sm">
            <Title order={4}>
              {t("common.entity.container.plural", "Containers")}
            </Title>
          </Group>
          <Divider pb="sm"/>
          <ContainerDataTable
            withBorder
            records={containers}
            fetching={isContainersLoading}
          />
        </Card>
      </Grid.Col>

      <Grid.Col span={12}>
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group position="apart" pb="sm">
            <Title order={4}>{t("common.entity.well.plural", "Wells")}</Title>
          </Group>
          <Divider pb="sm"/>
          <WellDataTable
            withBorder
            records={wells}
            fetching={isWellsLoading}
          />
        </Card>
      </Grid.Col>

      <HasAccess permissions={["restriction_view"]}>
        <Grid.Col span={12}>
          <RestrictionDataTableCard entity={containerCluster}/>
        </Grid.Col>
      </HasAccess>

      <Grid.Col span={12}>
        <HasAccess permissions={["object_mapping_view"]}>
          <ObjectMappingDataTableCard entity={containerCluster}/>
        </HasAccess>
      </Grid.Col>

      {opened &&
        <Modal opened={opened} onClose={close} size="auto" title="Entity Graph">
          <Container h={700} w={800}>
            <EntityGraphView entity={containerCluster} />
          </Container>
        </Modal>
      }
    </Grid>
  );
};

